<template>
  <div class="account-verification-page">
    <wallet-header :walletData="walletData" :isLoading="isLoading">
      <template #action-button><span></span></template>
    </wallet-header>

    <div v-if="walletData && !isLoading" class="verification-disclaimer-banner" :class="{
      'rejected': walletData?.user?.verificationStatus === 'rejected',
      'pending': walletData?.user?.verificationStatus === 'pending'
    }">
      <div class="container">
        <i :class="walletData?.user?.verificationStatus === 'rejected' ? 'bi bi-exclamation-triangle-fill' : 'bi bi-info-circle-fill'"></i>
        <span v-if="walletData?.user?.verificationStatus === 'rejected'">
          Your previous verification was rejected. Please resubmit your documents to withdraw funds.
        </span>
        <span v-else-if="walletData?.user?.verificationStatus === 'pending'">
          Your verification documents are being reviewed. We'll notify you once the process is complete.
        </span>
        <span v-else>
          Account verification is required before you can withdraw funds from your wallet.
        </span>
      </div>
    </div>

    <div class="container">
      <h5 v-if="isLoading">Loading...</h5>
      <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

      <!-- Pending verification message -->
      <div v-else-if="walletData?.user?.verificationStatus === 'pending'" class="py-5">
        <div class="verification-pending-card text-center">
          <div class="pending-icon mb-4">
            <i class="bi bi-hourglass-split"></i>
          </div>
          <h2>Verification In Progress</h2>
          <p class="text-muted mb-4">
            Your account verification is currently being reviewed. This process typically takes 1-3 business days.
          </p>
          <p class="text-muted mb-4">
            We will notify you via email once your verification is complete.
          </p>
          <button class="btn btn-warning" @click="$router.push('/wallet')">
            Back to Wallet
          </button>
        </div>
      </div>

      <!-- Verification form for unverified or rejected users -->
      <div v-else class="py-5">
        <h2>Account verification</h2>
        <p>Enter your details below to get your account verified</p>

        <form @submit.prevent="handleSubmit">
          <verification-form
            :formData="formData"
            :selectedCountry="selectedCountry"
            :nationalPhoneNumber="nationalPhoneNumber"
            :countries="countries"
            :errors="errors"
            :isPhoneValid="isPhoneValid"
            :phoneInputTouched="phoneInputTouched"
            :isPhoneOTPRequested="isPhoneOTPRequested"
            :isEmailOTPRequested="isEmailOTPRequested"
            :phoneOTPButtonText="phoneOTPButtonText"
            :emailOTPButtonText="emailOTPButtonText"
            :passportFileName="passportFileName"
            :binanceScreenshotFileNames="binanceScreenshotFileNames"
            @update:binance_email="formData.binance_email = $event"
            @update:wallet_address="formData.wallet_address = $event"
            @update:terms_accepted="formData.terms_accepted = $event"
            @update:phone_otp="formData.phone_otp = $event"
            @update:email_otp="formData.email_otp = $event"
            @country-changed="handleCountryChanged"
            @phone-input="handlePhoneInput"
            @request-phone-otp="requestPhoneOTP"
            @request-email-otp="requestEmailOTP"
            @passport-uploaded="handlePassportUpload"
            @binance-screenshots-uploaded="handleBinanceScreenshotsUpload"
            @remove-binance-file="removeBinanceFile"
          />

          <div class="submit-wrapper">
            <button type="submit" :disabled="isSubmitting">
              {{ isSubmitting ? "Submitting..." : "Submit" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import WalletHeader from "@/components/wallet/WalletHeader.vue";
import VerificationForm from "@/components/wallet/VerificationForm.vue";
import walletMixin from "@/mixins/wallet/walletMixin";
import verificationMixin from "@/mixins/wallet/verificationMixin";

export default {
  name: "AccountVerification",
  components: {
    WalletHeader,
    VerificationForm,
  },
  head: {
    link: [
      {
        rel: "stylesheet",
        href: "/assets/css/wallet.css",
      },
    ],
  },
  mixins: [walletMixin, verificationMixin],
};
</script>

<style scoped>
.verification-disclaimer-banner {
  background-color: #fff3cd;
  border-bottom: 1px solid #fcb913;
  padding: 12px 0;
}

.verification-disclaimer-banner .container {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #856404;
  font-weight: 500;
}

.verification-disclaimer-banner i {
  color: #fcb913;
  font-size: 1.25rem;
}

.verification-disclaimer-banner.rejected {
  background-color: #f8d7da;
  border-bottom: 1px solid #f34721;
}

.verification-disclaimer-banner.rejected .container {
  color: #721c24;
}

.verification-disclaimer-banner.rejected i {
  color: #f34721;
}

.verification-disclaimer-banner.pending {
  background-color: #d1ecf1;
  border-bottom: 1px solid #17a2b8;
}

.verification-disclaimer-banner.pending .container {
  color: #0c5460;
}

.verification-disclaimer-banner.pending i {
  color: #17a2b8;
}

/* Pending Verification Card */
.verification-pending-card {
  background-color: #fff;
  border-radius: 12px;
  padding: 3rem 2rem;
  max-width: 500px;
  margin: 0 auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.verification-pending-card h2 {
  color: #23292e;
  font-weight: 700;
  margin-bottom: 1rem;
}

.pending-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #fff3cd 0%, #fcb913 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.pending-icon i {
  font-size: 2.5rem;
  color: #856404;
}
</style>
